// services/meterFormat.js — the meter under the chat's composer, in words.
//
// The server measures three windows (chat-2, docs/plans/chat-2.md "The
// meter"): the five-hour window, the week and the month, each as a share
// used and when it starts again. Percentages only, never dollars. The chat
// says "Plenty left" until the window or the week passes WARN_PCT, and then
// says which one and when it is back, in the person's own time: "This window
// 82% · resets 3:10 pm" (founder, 2026-10-30). The month shows only in the
// popover; a month nearly spent is the Account page's to say, with its bars.

export const WARN_PCT = 70;

/**
 * The browser's IANA time zone ("America/Los_Angeles"), or null where Intl
 * cannot say. Sent with the chat and the usage read, so the server says
 * "back at 3:10 pm" in the person's own time.
 */
export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

const pctOf = (part) => {
  const v = Number(part?.usedPct);
  return Number.isFinite(v) ? Math.max(0, Math.min(100, Math.round(v))) : null;
};

const validDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
};

// One formatter per set of options and zone, made once: toLocaleTimeString
// and its siblings build a new Intl.DateTimeFormat on every call, and the
// meter under the composer is drawn on every keystroke (2026-10-31).
const formatters = new Map();
const formatter = (options) => {
  const key = JSON.stringify(options);
  let f = formatters.get(key);
  if (!f) { f = new Intl.DateTimeFormat('en-US', options); formatters.set(key, f); }
  return f;
};

// "3:10 PM" -> "3:10 pm": how the server's own line says it.
const lowerMeridiem = (s) => s.replace(/\s?([AP])M\b/g, (_, a) => ` ${a.toLowerCase()}m`).replace(/ /g, ' ');

/** "3:10 pm", in the person's time zone. */
export const timeOfDay = (iso, { timeZone } = {}) => {
  const d = validDate(iso);
  return d ? lowerMeridiem(formatter({ hour: 'numeric', minute: '2-digit', timeZone }).format(d)) : '';
};

/** "Mon 9:00 am", in the person's time zone. */
export const weekdayTime = (iso, { timeZone } = {}) => {
  const d = validDate(iso);
  if (!d) return '';
  const day = formatter({ weekday: 'short', timeZone }).format(d);
  return `${day} ${timeOfDay(iso, { timeZone })}`;
};

// The month turns over at midnight UTC (the server's month `resetsAt` is
// "…-01T00:00:00.000Z"), so its day is read in UTC: in California that
// instant is the evening before, and
// "resets Oct 31" for a November allowance would read as a mistake. Same
// choice as usageFormat.js resetsOn.
/** "Nov 1". */
export const monthDay = (iso) => {
  const d = validDate(iso);
  return d ? formatter({ month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d) : '';
};

/**
 * What the composer's meter shows: the bar's fill (the higher of the window
 * and the week, so it fills as the nearer limit does), whether it warns, and
 * its words.
 */
export function meterView(meter, { timeZone } = {}) {
  if (!meter) return null;
  const win = pctOf(meter.window);
  const week = pctOf(meter.week);
  const pct = Math.max(win ?? 0, week ?? 0);
  const warn = (win ?? 0) >= WARN_PCT || (week ?? 0) >= WARN_PCT;
  if (!warn) return { pct, warn: false, label: 'Plenty left' };
  if (typeof meter.line === 'string' && meter.line.trim()) return { pct, warn: true, label: meter.line.trim() };
  const label = (win ?? 0) >= (week ?? 0)
    ? `This window ${win}%${meter.window?.resetsAt ? ` · resets ${timeOfDay(meter.window.resetsAt, { timeZone })}` : ''}`
    : `This week ${week}%${meter.week?.resetsAt ? ` · resets ${weekdayTime(meter.week.resetsAt, { timeZone })}` : ''}`;
  return { pct, warn: true, label };
}

/** The popover's three lines: the window, the week, the month. */
export function meterRows(meter, { timeZone } = {}) {
  if (!meter) return [];
  const rows = [
    { key: 'window', name: 'This window', part: meter.window, when: (iso) => timeOfDay(iso, { timeZone }) },
    { key: 'week', name: 'This week', part: meter.week, when: (iso) => weekdayTime(iso, { timeZone }) },
    { key: 'month', name: 'This month', part: meter.month, when: (iso) => monthDay(iso) },
  ];
  return rows
    .filter(r => r.part && pctOf(r.part) !== null)
    .map(r => {
      const when = r.part.resetsAt ? r.when(r.part.resetsAt) : '';
      return { key: r.key, name: r.name, pct: pctOf(r.part), resets: when ? `resets ${when}` : '' };
    });
}

/**
 * How many deep turns ("Think harder") are left today: a number, or null
 * when there is no daily cap (Max). 0 on Free, where the cap is 0.
 */
export function deepLeft(meter) {
  const deep = meter?.deep;
  if (!deep || deep.capPerDay === null || deep.capPerDay === undefined) return null;
  return Math.max(0, (Number(deep.capPerDay) || 0) - (Number(deep.usedToday) || 0));
}
